import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  LinearProgress,
  Divider,
} from "@material-ui/core";
import { Close as CloseIcon, FlashOn as FlashOnIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { useProcesarAutomaticoMutation } from "src/services/conciliacionApi";
import { useSnackbar } from "src/components/snackbar";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  optionsBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default function ModalProcesarAutomatico({
  open,
  onClose,
  loteId = null,
  movimientoIds = null,
  onSuccess,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [autoConciliar, setAutoConciliar] = useState(false);

  const [procesarAutomatico, { isLoading }] = useProcesarAutomaticoMutation();

  // Calcular cantidad de movimientos
  const cantidadMovimientos = movimientoIds
    ? movimientoIds.length
    : "todos los pendientes";

  const handleProcesar = async () => {
    try {
      const body = {
        auto_conciliar: autoConciliar,
      };

      // Agregar lote_id o movimiento_ids según corresponda
      if (loteId) {
        body.lote_id = loteId;
      } else if (movimientoIds && movimientoIds.length > 0) {
        body.movimiento_ids = movimientoIds;
      }

      const resultado = await procesarAutomatico(body).unwrap();

      enqueueSnackbar("Procesamiento completado exitosamente", {
        variant: "success",
      });

      // Llamar callback con resultados
      if (onSuccess) {
        onSuccess(resultado.resultados);
      }

      onClose();
    } catch (error) {
      console.error("Error al procesar:", error);
      enqueueSnackbar(error?.data?.message || "Error al procesar movimientos", {
        variant: "error",
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <FlashOnIcon />
          <Typography variant="h6">Procesamiento Automático</Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Información */}
        <Box className={classes.infoBox}>
          <Typography variant="subtitle2" gutterBottom>
            📊 Información
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Movimientos a procesar: <strong>{cantidadMovimientos}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Tiempo estimado:{" "}
            <strong>
              {movimientoIds && movimientoIds.length < 20
                ? "~5 segundos"
                : "~15-30 segundos"}
            </strong>
          </Typography>
        </Box>

        <Divider />

        {/* Opciones */}
        <Box className={classes.optionsBox}>
          <Typography variant="subtitle2" gutterBottom>
            ⚙️ Opciones
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoConciliar}
                onChange={(e) => setAutoConciliar(e.target.checked)}
                color="primary"
                disabled={isLoading}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Auto-conciliar matches perfectos
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  (Score ≥ 90 puntos)
                </Typography>
              </Box>
            }
          />
        </Box>

        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            El sistema buscará automáticamente facturas que coincidan con cada
            movimiento bancario.
          </Typography>
        </Alert>

        {/* Loading Progress */}
        {isLoading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              display="block"
              style={{ marginTop: 8 }}
            >
              Procesando movimientos...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleProcesar}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={<FlashOnIcon />}
        >
          {isLoading ? "Procesando..." : "Procesar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
